import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { X, Save, CalendarClock, Plus, Copy, Trash2, ArrowUp, ArrowDown, Lock, Unlock } from "lucide-react";
import { BLOCK_LABELS, DEFAULT_LAYOUT_JSON, type LayoutJson, type LayoutBlockId, type LayoutCanvasNode, type CanvasArtboardId } from "@/lib/layout-studio/types";
import { useAppAssets } from "@/hooks/useAppAssets";

// Import Magical UX Layer
import {
  MagicalUXProvider,
  MagicalCursor,
  EncouragingFeedback,
  SmartSuggestions,
  CelebrationEffects,
  ComponentLibraryHint,
  CanvasAreaHint,
  useMagicalUX,
} from "./magical-ux";
import { GuidedBuilderPanel } from "./guided-builder/GuidedBuilderPanel";

const GRID_SIZE = 8;
const MIN_NODE_W = 180;
const MIN_NODE_H = 80;

interface CanvasEditorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  layout: LayoutJson;
  presetName: string;
  onChange: (layout: LayoutJson) => void;
  onSave: () => Promise<boolean>;
  onOpenSchedule: () => void;
  isBeginnerMode?: boolean;
}

function snap(n: number) {
  return Math.round(n / GRID_SIZE) * GRID_SIZE;
}

function ensureCanvasLayout(layout: LayoutJson): LayoutJson {
  if (layout.canvas) {
    return { ...layout, editorMode: "canvas" };
  }
  return {
    ...layout,
    editorMode: "canvas",
    canvas: DEFAULT_LAYOUT_JSON.canvas,
  };
}

// Inner component that uses magical UX context
function CanvasEditorContent({
  layout,
  presetName,
  onChange,
  onSave,
  onOpenSchedule,
  onOpenChange,
  isBeginnerMode,
}: Omit<CanvasEditorDialogProps, 'open'>) {
  const { recordAction, setCursorMode, state } = useMagicalUX();
  const { assets } = useAppAssets({ status: "active" });
  const [search, setSearch] = useState("");
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [dragState, setDragState] = useState<null | {
    type: "move" | "resize";
    nodeId: string;
    startX: number;
    startY: number;
    startNode: LayoutCanvasNode;
  }>(null);
  const canvasRef = useRef<HTMLDivElement | null>(null);

  const safeLayout = useMemo(() => ensureCanvasLayout(layout), [layout]);
  const activeArtboard = (safeLayout.canvas?.activeArtboard || "desktop") as CanvasArtboardId;
  const artboard = safeLayout.canvas?.artboards?.[activeArtboard] || DEFAULT_LAYOUT_JSON.canvas!.artboards.desktop;
  const nodes = artboard.nodes || [];

  const filteredBlocks = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (Object.keys(BLOCK_LABELS) as LayoutBlockId[]).filter((id) => {
      const label = BLOCK_LABELS[id].toLowerCase();
      return !q || label.includes(q) || id.includes(q);
    });
  }, [search]);

  const selectedNode = useMemo(
    () => nodes.find((n) => n.id === selectedNodeId) || null,
    [nodes, selectedNodeId]
  );

  const updateNodes = useCallback((nextNodes: LayoutCanvasNode[]) => {
    onChange({
      ...safeLayout,
      editorMode: "canvas",
      canvas: {
        ...safeLayout.canvas!,
        artboards: {
          ...safeLayout.canvas!.artboards,
          [activeArtboard]: {
            ...artboard,
            nodes: nextNodes,
          },
        },
      },
    });
  }, [onChange, safeLayout, activeArtboard, artboard]);

  const updateBackground = useCallback((updater: (layers: NonNullable<LayoutJson["canvas"]>["backgroundLayers"]) => NonNullable<LayoutJson["canvas"]>["backgroundLayers"]) => {
    const current = safeLayout.canvas?.backgroundLayers || [];
    onChange({
      ...safeLayout,
      editorMode: "canvas",
      canvas: {
        ...safeLayout.canvas!,
        backgroundLayers: updater(current),
      },
    });
  }, [onChange, safeLayout]);

  const addNode = useCallback((blockId: LayoutBlockId, x = 40, y = 40) => {
    const next: LayoutCanvasNode = {
      id: `node-${blockId}-${Date.now()}`,
      blockId,
      rect: { x: snap(x), y: snap(y), w: 360, h: 180 },
      z: nodes.length + 1,
      visible: true,
      locked: false,
    };
    updateNodes([...nodes, next]);
    setSelectedNodeId(next.id);
    
    // Record magical UX action
    recordAction(`${blockId}-added`);
    setCursorMode('wand');
    setTimeout(() => setCursorMode(state.isBeginnerMode ? 'sparkle' : 'normal'), 2000);
  }, [nodes, updateNodes, recordAction, setCursorMode, state.isBeginnerMode]);

  const handleCanvasDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const blockId = e.dataTransfer.getData("text/layout-block") as LayoutBlockId;
    if (!blockId) return;
    const rect = canvasRef.current?.getBoundingClientRect();
    const x = rect ? e.clientX - rect.left - 140 : 40;
    const y = rect ? e.clientY - rect.top - 60 : 40;
    addNode(blockId, x, y);
  }, [addNode]);

  const patchNode = useCallback((nodeId: string, updater: (node: LayoutCanvasNode) => LayoutCanvasNode) => {
    updateNodes(nodes.map((n) => (n.id === nodeId ? updater(n) : n)));
  }, [nodes, updateNodes]);

  useEffect(() => {
    if (!dragState) return;

    const onMove = (e: MouseEvent) => {
      const dx = e.clientX - dragState.startX;
      const dy = e.clientY - dragState.startY;
      if (dragState.type === "move") {
        patchNode(dragState.nodeId, (n) => ({
          ...n,
          rect: {
            ...n.rect,
            x: Math.max(0, snap(dragState.startNode.rect.x + dx)),
            y: Math.max(0, snap(dragState.startNode.rect.y + dy)),
          },
        }));
      } else {
        patchNode(dragState.nodeId, (n) => ({
          ...n,
          rect: {
            ...n.rect,
            w: Math.max(MIN_NODE_W, snap(dragState.startNode.rect.w + dx)),
            h: Math.max(MIN_NODE_H, snap(dragState.startNode.rect.h + dy)),
          },
        }));
      }
    };

    const onUp = () => setDragState(null);

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [dragState, patchNode]);

  useEffect(() => {
    if (!selectedNodeId || !open) return;

    const onKey = (e: KeyboardEvent) => {
      const step = e.shiftKey ? 16 : GRID_SIZE;
      if (["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(e.key)) {
        e.preventDefault();
        patchNode(selectedNodeId, (n) => {
          if (n.locked) return n;
          if (e.key === "ArrowLeft") return { ...n, rect: { ...n.rect, x: Math.max(0, n.rect.x - step) } };
          if (e.key === "ArrowRight") return { ...n, rect: { ...n.rect, x: n.rect.x + step } };
          if (e.key === "ArrowUp") return { ...n, rect: { ...n.rect, y: Math.max(0, n.rect.y - step) } };
          return { ...n, rect: { ...n.rect, y: n.rect.y + step } };
        });
      }
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selectedNodeId, patchNode, open]);

  if (nodes.length === 0 && state.isBeginnerMode) {
    // Check if this might be their first build
    const completedSteps = state.completedSteps.length;
    if (completedSteps > 2) {
      recordAction('first-build');
    }
  }
  return (
    <div className="fixed inset-0 z-[120] bg-background">
      <div className="h-full flex flex-col">
        <div className="h-14 border-b px-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Badge variant="secondary">Canvas Editor</Badge>
            <span className="text-sm font-medium">{presetName || "Untitled preset"}</span>
            <Badge variant="outline">{activeArtboard}</Badge>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={onOpenSchedule}>
              <CalendarClock className="h-4 w-4 mr-1" />
              Schedule
            </Button>
            <Button size="sm" onClick={onSave}>
              <Save className="h-4 w-4 mr-1" />
              Save
            </Button>
            <Button variant="ghost" size="icon" onClick={() => onOpenChange(false)}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <div className="flex-1 grid grid-cols-[280px_1fr_320px] min-h-0">
          {state.isBeginnerMode ? (
            <div className="border-r min-h-0 flex flex-col">
              <GuidedBuilderPanel
                onAddBlocks={(blocks) => {
                  blocks.forEach((blockId, index) => {
                    // Stack blocks vertically with nice spacing
                    const y = 40 + index * 200;
                    addNode(blockId, 40, y);
                  });
                }}
                onComplete={() => {
                  recordAction('completed-guided-setup');
                  // Could trigger mode switch or other celebratory actions
                }}
              />
            </div>
          ) : (
            <div className="border-r p-3 min-h-0 flex flex-col gap-3">
              <Label className="text-xs">Organisms Library</Label>
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search organisms..."
                className="h-8"
              />
              <ScrollArea className="flex-1">
                <div className="space-y-2 pr-2">
                  {filteredBlocks.map((blockId) => (
                    <ComponentLibraryHint key={blockId}>
                      <div
                        className="border rounded-md p-3 bg-card cursor-grab active:cursor-grabbing hover:bg-accent/50 transition-colors"
                        draggable
                        onDragStart={(e) => e.dataTransfer.setData("text/layout-block", blockId)}
                        onDoubleClick={() => addNode(blockId)}
                      >
                        <div className="font-medium text-sm">{BLOCK_LABELS[blockId]}</div>
                        <div className="text-xs text-muted-foreground mt-1">Drag to canvas or double-click to add</div>
                      </div>
                    </ComponentLibraryHint>
                  ))}
                </div>
              </ScrollArea>
            </div>
          )}

          <div className="min-h-0 overflow-auto bg-muted/20">
            <div className="p-6">
              <CanvasAreaHint>
                <div
                  ref={canvasRef}
                  className="relative mx-auto bg-background border rounded-md shadow-sm"
                  style={{
                    width: artboard.width,
                    minHeight: artboard.minHeight,
                    backgroundImage:
                      "linear-gradient(to right, rgba(120,120,120,0.08) 1px, transparent 1px), linear-gradient(to bottom, rgba(120,120,120,0.08) 1px, transparent 1px)",
                    backgroundSize: `${GRID_SIZE * 4}px ${GRID_SIZE * 4}px`,
                  }}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={handleCanvasDrop}
                  onMouseDown={() => setSelectedNodeId(null)}
                >
                {(safeLayout.canvas?.backgroundLayers || [])
                  .filter((l) => l.visible)
                  .sort((a, b) => a.z - b.z)
                  .map((layer) => {
                    if (layer.kind === "solid") {
                      return (
                        <div
                          key={layer.id}
                          className="absolute inset-0 pointer-events-none"
                          style={{ backgroundColor: `hsl(${layer.solidColor || "0 0% 98%"})`, opacity: layer.opacity }}
                        />
                      );
                    }
                    if (layer.kind === "gradient") {
                      const g = layer.gradient || { color1: "0 0% 98%", color2: "220 15% 95%", angle: 135 };
                      return (
                        <div
                          key={layer.id}
                          className="absolute inset-0 pointer-events-none"
                          style={{ background: `linear-gradient(${g.angle}deg, hsl(${g.color1}), hsl(${g.color2}))`, opacity: layer.opacity }}
                        />
                      );
                    }
                    return (
                      <div
                        key={layer.id}
                        className="absolute inset-0 pointer-events-none"
                        style={{
                          backgroundImage: "radial-gradient(hsl(220 10% 92%) 1px, transparent 1px)",
                          backgroundSize: "20px 20px",
                          opacity: layer.opacity,
                        }}
                      />
                    );
                  })}

                {nodes
                  .filter((n) => n.visible)
                  .sort((a, b) => a.z - b.z)
                  .map((node) => (
                    <div
                      key={node.id}
                      className={`absolute border rounded bg-background/90 shadow-sm overflow-hidden ${selectedNodeId === node.id ? "ring-2 ring-primary" : ""}`}
                      style={{
                        left: node.rect.x,
                        top: node.rect.y,
                        width: node.rect.w,
                        height: node.rect.h,
                        zIndex: node.z,
                      }}
                      onMouseDown={(e) => {
                        e.stopPropagation();
                        setSelectedNodeId(node.id);
                        if (node.locked) return;
                        setDragState({
                          type: "move",
                          nodeId: node.id,
                          startX: e.clientX,
                          startY: e.clientY,
                          startNode: node,
                        });
                      }}
                    >
                      <div className="h-8 px-2 border-b bg-muted/40 flex items-center justify-between text-xs">
                        <span className="font-medium truncate">{BLOCK_LABELS[node.blockId]}</span>
                        <span className="text-muted-foreground">#{node.z}</span>
                      </div>
                      <div className="p-2 text-xs text-muted-foreground">
                        Dragged organism placeholder. Live rendering uses LayoutRenderer.
                      </div>
                      {!node.locked && (
                        <button
                          className="absolute bottom-1 right-1 w-3.5 h-3.5 rounded-sm bg-primary"
                          onMouseDown={(e) => {
                            e.stopPropagation();
                            setSelectedNodeId(node.id);
                            setDragState({
                              type: "resize",
                              nodeId: node.id,
                              startX: e.clientX,
                              startY: e.clientY,
                              startNode: node,
                            });
                          }}
                          aria-label="Resize"
                        />
                      )}
                     </div>
                   ))}
                 </div>
               </CanvasAreaHint>
             </div>
           </div>

          <div className="border-l p-3 min-h-0 flex flex-col gap-3">
            <div>
              <Label className="text-xs">Artboard</Label>
              <Select
                value={activeArtboard}
                onValueChange={(v) =>
                  onChange({
                    ...safeLayout,
                    editorMode: "canvas",
                    canvas: { ...safeLayout.canvas!, activeArtboard: v as CanvasArtboardId },
                  })
                }
              >
                <SelectTrigger className="h-8 mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="desktop">Desktop</SelectItem>
                  <SelectItem value="tablet">Tablet</SelectItem>
                  <SelectItem value="mobile">Mobile</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Separator />

            <div className="min-h-0 flex-1 overflow-auto space-y-3 pr-1">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label className="text-xs">Layers</Label>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7"
                    onClick={() =>
                      updateBackground((layers) => [
                        ...layers,
                        {
                          id: `bg-${Date.now()}`,
                          kind: "solid",
                          visible: true,
                          z: layers.length,
                          opacity: 1,
                          solidColor: "0 0% 98%",
                        },
                      ])
                    }
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    BG Layer
                  </Button>
                </div>
                {(safeLayout.canvas?.backgroundLayers || []).map((layer) => (
                  <div key={layer.id} className="border rounded p-2 text-xs space-y-2">
                    <div className="flex items-center justify-between">
                      <Badge variant="outline">{layer.kind}</Badge>
                      <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => updateBackground((layers) => layers.filter((l) => l.id !== layer.id))}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                    <Select
                      value={layer.kind}
                      onValueChange={(v) => updateBackground((layers) => layers.map((l) => l.id === layer.id ? { ...l, kind: v as any } : l))}
                    >
                      <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="solid">Solid</SelectItem>
                        <SelectItem value="gradient">Gradient</SelectItem>
                        <SelectItem value="asset">Asset</SelectItem>
                      </SelectContent>
                    </Select>
                    {layer.kind === "asset" && (
                      <Select
                        value={layer.asset?.assetId || ""}
                        onValueChange={(v) => updateBackground((layers) => layers.map((l) => l.id === layer.id ? { ...l, asset: { assetId: v, fit: "cover" } } : l))}
                      >
                        <SelectTrigger className="h-7 text-xs"><SelectValue placeholder="Asset" /></SelectTrigger>
                        <SelectContent>
                          {assets.map((a) => (
                            <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                ))}
              </div>

              <Separator />

              <div>
                <Label className="text-xs">Selected Organism</Label>
                {!selectedNode ? (
                  <div className="text-xs text-muted-foreground mt-2">Select a node on canvas</div>
                ) : (
                  <div className="space-y-2 mt-2">
                    <div className="text-xs font-medium">{BLOCK_LABELS[selectedNode.blockId]}</div>
                    <div className="grid grid-cols-2 gap-2">
                      <Input className="h-7 text-xs" type="number" value={selectedNode.rect.x} onChange={(e) => patchNode(selectedNode.id, (n) => ({ ...n, rect: { ...n.rect, x: snap(Number(e.target.value) || 0) } }))} />
                      <Input className="h-7 text-xs" type="number" value={selectedNode.rect.y} onChange={(e) => patchNode(selectedNode.id, (n) => ({ ...n, rect: { ...n.rect, y: snap(Number(e.target.value) || 0) } }))} />
                      <Input className="h-7 text-xs" type="number" value={selectedNode.rect.w} onChange={(e) => patchNode(selectedNode.id, (n) => ({ ...n, rect: { ...n.rect, w: Math.max(MIN_NODE_W, snap(Number(e.target.value) || MIN_NODE_W)) } }))} />
                      <Input className="h-7 text-xs" type="number" value={selectedNode.rect.h} onChange={(e) => patchNode(selectedNode.id, (n) => ({ ...n, rect: { ...n.rect, h: Math.max(MIN_NODE_H, snap(Number(e.target.value) || MIN_NODE_H)) } }))} />
                    </div>
                    <div className="flex items-center gap-1">
                      <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => patchNode(selectedNode.id, (n) => ({ ...n, z: Math.max(1, n.z - 1) }))}><ArrowDown className="h-3 w-3" /></Button>
                      <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => patchNode(selectedNode.id, (n) => ({ ...n, z: n.z + 1 }))}><ArrowUp className="h-3 w-3" /></Button>
                      <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => patchNode(selectedNode.id, (n) => ({ ...n, locked: !n.locked }))}>{selectedNode.locked ? <Unlock className="h-3 w-3" /> : <Lock className="h-3 w-3" />}</Button>
                      <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => {
                        const copy: LayoutCanvasNode = {
                          ...selectedNode,
                          id: `node-${selectedNode.blockId}-${Date.now()}`,
                          rect: { ...selectedNode.rect, x: selectedNode.rect.x + 24, y: selectedNode.rect.y + 24 },
                          z: nodes.length + 1,
                        };
                        updateNodes([...nodes, copy]);
                        setSelectedNodeId(copy.id);
                      }}><Copy className="h-3 w-3" /></Button>
                      <Button size="icon" variant="destructive" className="h-7 w-7" onClick={() => {
                        updateNodes(nodes.filter((n) => n.id !== selectedNode.id));
                        setSelectedNodeId(null);
                      }}><Trash2 className="h-3 w-3" /></Button>
                    </div>
                  </div>
                )}
                </div>
              </div>
            </div>
          </div>
      </div>
    </div>
  );
}

export function CanvasEditorDialog({
  open,
  onOpenChange,
  isBeginnerMode = false,
  ...props
}: CanvasEditorDialogProps) {
  if (!open) return null;

  return (
    <MagicalUXProvider isBeginnerMode={isBeginnerMode}>
      {/* Magical UX Components */}
      <MagicalCursor />
      <EncouragingFeedback />
      <SmartSuggestions />
      <CelebrationEffects />
      
      <CanvasEditorContent {...props} onOpenChange={onOpenChange} isBeginnerMode={isBeginnerMode} />
    </MagicalUXProvider>
  );
}
